import firebase from 'firebase/app';
import 'firebase/firebase-messaging';
import { firebaseConfig, firebaseKeyPair } from '../constants';
import { conversationMessageNotificationType } from '../constants/conversations';
import { fcmTokenStorageKey } from '../api/httpClient';
import { deleteFcmToken, postFcmToken } from '../api';
import notificationIcon from '../images/notification_icon.png';

export const notifIsInitializedStorageKey = 'notifIsInitialized';

/**
 * @class NotificationsService
 * @description
 * Used for:
 *  - request notifications permission
 *  - get and store fcm token
 *  - subscribe for notifications
 *  - unsubscribe app
 */

class NotificationsService {
  constructor () {
    this.isSupported = firebase.messaging.isSupported();
    this.messaging = null;
    this.serviceWorkerRegistration = null;
    this.user_id = null;
    this.platform = 'web';
    this.fcmToken = null;
    this.unsubscribeMessageListener = null;
    this.unsubscribeTokenRefreshListener = null;
    this.callbackOnNotification = null;
    this.callbackOnNotificationFromChat = null;
  }

  init (user_id) {
    try {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.register('../firebase-messaging-sw.js');
        navigator.serviceWorker.ready.then(registration => {
          this.serviceWorkerRegistration = registration;
        });
      }
      this.user_id = user_id;

      if (!firebase.apps.length) {
        firebase.initializeApp(firebaseConfig);
        this.messaging = firebase.messaging();
        this.messaging.usePublicVapidKey(firebaseKeyPair);
      }

      this.setToken().then(this.createListeners);
    } catch (error) {
      throw new Error(`Failed to initialize notifications. \n Details: \n ${error}`);
    }
  }

  async setToken () {
    let fcmToken = window.localStorage.getItem(fcmTokenStorageKey);
    if (!fcmToken) {
      fcmToken = await this.messaging.requestPermission()
        .then(() => this._getToken())
        .catch(error => console.log(error));
    }
    this.fcmToken = fcmToken;
  }

  _getToken = async () => {
    return this.messaging.getToken().then(token => {
      window.localStorage.setItem(fcmTokenStorageKey, token);
      postFcmToken({
        token,
        user_id: this.user_id,
        platform: this.platform
      });
      return token;
    });
  };

  setCallbackOnNotification = callback => {
    this.callbackOnNotification = callback;
  };

  setCallbackOnNotificationFromChat = callback => {
    this.callbackOnNotificationFromChat = callback;
  };

  createListeners = () => {
    this.unsubscribeTokenRefreshListener = this.messaging.onTokenRefresh(() => {
      deleteFcmToken(this.fcmToken);
      this._getToken().then(token => {
        this.fcmToken = token;
      });
    });
    const isInitialized = window.localStorage.getItem(notifIsInitializedStorageKey);
    if (!isInitialized) {
      window.localStorage.setItem(notifIsInitializedStorageKey, true);
    }
    this.unsubscribeMessageListener = this.messaging.onMessage(payload => {
      if (payload.data.type === conversationMessageNotificationType) {
        const { title, body } = payload.notification;
        const options = {
          body,
          icon: notificationIcon,
          data: payload.data
        };
        if (!isInitialized) {
          this.serviceWorkerRegistration.showNotification(title, options);
        }
        const message = JSON.parse(payload.data.message);
        if (this.callbackOnNotification) this.callbackOnNotification(message.conversation_id);
        if (this.callbackOnNotificationFromChat) this.callbackOnNotificationFromChat(message);
      }
    });
  };

  unsubscribe = () => {
    if (firebase.apps.length) {
      deleteFcmToken(this.fcmToken);
      this.unsubscribeMessageListener();
      this.unsubscribeTokenRefreshListener();
      this.fcmToken = null;
      window.localStorage.removeItem(fcmTokenStorageKey);
      window.localStorage.removeItem(notifIsInitializedStorageKey);
    }
  };
}

export default new NotificationsService();



// WEBPACK FOOTER //
// ./src/services/NotificationsService.js